import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium';
import { DataTable } from '@peritasai/ui-components';
import type { CountSheetEntity } from '@peritasai/repository';
import { useTranslation } from 'react-i18next';
import { renderDateCell, renderTableCell } from '../shared/renderTableCell';

interface CountSheetsTableProps {
  rows: CountSheetEntity[];
  isLoading: boolean;
  hasSearch: boolean;
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onRowClick: (row: CountSheetEntity) => void;
}

export const CountSheetsTable = ({
  rows,
  isLoading,
  hasSearch,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  onRowClick,
}: CountSheetsTableProps) => {
  const { t } = useTranslation(['periverse']);

  const columns: GridColDef<CountSheetEntity>[] = [
    {
      field: 'trayName',
      headerName: t('pages.entityManagement.countSheetTable.columns.trayName'),
      flex: 2,
      renderCell: (params) => renderTableCell(params.value, true),
    },
    {
      field: 'trayId',
      headerName: t('pages.entityManagement.countSheetTable.columns.trayId'),
      flex: 1,
      valueGetter: (_value: unknown, row: CountSheetEntity) => row.productNumber ?? '',
      renderCell: (params) => renderTableCell(params.value, false, ''),
    },
    {
      field: 'instrumentCount',
      headerName: t('pages.entityManagement.countSheetTable.columns.instruments'),
      flex: 1,
      valueGetter: (_value: unknown, row: CountSheetEntity) => row.instrumentCount,
      renderCell: (params) => renderTableCell(params.value),
    },
    {
      field: 'createdAt',
      headerName: t('pages.entityManagement.countSheetTable.columns.createdAt'),
      flex: 1,
      valueFormatter: (value: string) => new Date(value).toLocaleDateString(),
      renderCell: (params) => renderDateCell(params.value),
    },
    {
      field: 'updatedAt',
      headerName: t('pages.entityManagement.countSheetTable.columns.updatedAt'),
      flex: 1,
      headerClassName: 'last-column-header',
      valueFormatter: (value: string) => new Date(value).toLocaleDateString(),
      renderCell: (params) => renderDateCell(params.value),
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      rowCount={rowCount}
      isLoading={isLoading}
      hasSearch={hasSearch}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      onRowClick={onRowClick}
      searchEmptyTitle={t('pages.entityManagement.table.searchEmptyTitle')}
      searchEmptyMessage={t('pages.entityManagement.table.searchEmptyMessage')}
      emptyMessage={t('pages.entityManagement.table.noRows', {
        entityName: t('pages.entityManagement.items.countSheets'),
      })}
      skeletonColumnCount={5}
    />
  );
};
